import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { getApiErrorMessage, inr, formatDate } from "@/lib/utils_pharma";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, Trash2, Printer, Receipt, Plus, AlertTriangle, Clock, Minus, CheckCircle2, User, X } from "lucide-react";
import InvoicePrint from "@/components/InvoicePrint";
import InvoicePreviewDialog from "@/components/InvoicePreviewDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const isExpired = (expiryDate) => {
  if (!expiryDate) return false;
  const todayStr = new Date().toISOString().slice(0, 10);
  return expiryDate < todayStr;
};

const isExpiringSoon = (expiryDate, days = 60) => {
  if (!expiryDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDate);
  expiry.setHours(0, 0, 0, 0);
  const diffTime = expiry - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays >= 0 && diffDays <= days;
};

const formatStockByUnits = (stock, med) => {
  const baseStock = Math.max(0, Number(stock) || 0);
  const packingUnit = med.packing_unit || med.unit || "Pack";
  const baseUnit = med.base_unit || packingUnit;
  const unitsPerPack = Math.max(1, Number(med.units_per_pack) || 1);

  if (packingUnit !== baseUnit && unitsPerPack > 1) {
    const packStock = Math.floor(baseStock / unitsPerPack);
    const looseStock = baseStock % unitsPerPack;
    return `${packStock} ${packingUnit} + ${looseStock} ${baseUnit} (${baseStock} ${baseUnit} total)`;
  }

  return `${baseStock} ${packingUnit}`;
};

const toBaseQuantity = (quantity, sellingUnit, item) => {
  const unitsPerPack = Number(item.units_per_pack || 1);
  const isBaseUnit = sellingUnit === item.base_unit && item.packing_unit !== item.base_unit;
  return isBaseUnit ? Number(quantity || 0) : Number(quantity || 0) * unitsPerPack;
};

const getMaxQuantityForSellingUnit = (item, sellingUnit = item.selected_selling_unit || item.packing_unit || "Strip") => {
  const stock = Math.max(0, Number(item._stock || 0));
  const unitsPerPack = Math.max(1, Number(item.units_per_pack || 1));
  const isBaseUnit = sellingUnit === item.base_unit && item.packing_unit !== item.base_unit;
  return isBaseUnit ? stock : Math.floor(stock / unitsPerPack);
};

const formatRemainingStock = (item) => {
  const sellingUnit = item.selected_selling_unit || item.packing_unit || "Strip";
  const inCartBaseQty = toBaseQuantity(item.quantity, sellingUnit, item);
  return formatStockByUnits(Math.max(0, Number(item._stock || 0) - inCartBaseQty), item);
};

export default function Billing() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState({ name: "", phone: "" });
  const [customerSearchQuery, setCustomerSearchQuery] = useState("");
  const [billDisc, setBillDisc] = useState(0);
  const [paymentMode, setPaymentMode] = useState("Cash");
  const [appliedSplit, setAppliedSplit] = useState(null);
  const [splitModalOpen, setSplitModalOpen] = useState(false);
  const [splitRows, setSplitRows] = useState([
    { mode: "Cash", amount: "" },
    { mode: "UPI", amount: "" },
  ]);
  const prevPaymentModeRef = useRef("Cash");
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30); // Default to 30 days due date
    return d.toISOString().slice(0, 10);
  });
  const [paidAmount, setPaidAmount] = useState(0);
  const [paymentNotes, setPaymentNotes] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [lastBill, setLastBill] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [shopInfo, setShopInfo] = useState({});
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickForm, setQuickForm] = useState({
    name: "", manufacturer: "", batch_no: "", hsn_code: "",
    mrp: 0, purchase_price: 0, gst_percent: 12, stock: 0, low_stock_threshold: 10, expiry_date: "",
  });
  const [quickSaving, setQuickSaving] = useState(false);
  const [patientResults, setPatientResults] = useState([]);
  const [showPatientResults, setShowPatientResults] = useState(false);
  const [activeSearchField, setActiveSearchField] = useState("name");
  const [patientQuickOpen, setPatientQuickOpen] = useState(false);
  const [patientQuickForm, setPatientQuickForm] = useState({
    name: "", phone: "", email: "", age: "", gender: "", address: "", medical_history: ""
  });
  const [patientQuickSaving, setPatientQuickSaving] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    api.get("/settings/shop").then((r) => setShopInfo(r.data)).catch(() => { });
  }, []);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      const { data } = await api.get("/medicines", { params: { q, limit: 20, include_batches: true } });
      setResults(data);
      setShowResults(true);
    }, 150);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const query = (customerSearchQuery || "").trim();
    if (!query) {
      setPatientResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/patients", { params: { q: query } });
        const term = query.toLowerCase();
        const termDigits = term.replace(/\D/g, "");
        const filtered = (data || []).filter((p) => {
          const pName = (p.name || "").toString().toLowerCase();
          const pPhone = (p.phone || "").toString().toLowerCase();
          const pPhoneDigits = pPhone.replace(/\D/g, "");
          const nameMatch = pName.includes(term);
          const phoneMatch = pPhone.includes(term) || (termDigits.length > 0 && pPhoneDigits.includes(termDigits));
          return nameMatch || phoneMatch;
        });
        setPatientResults(filtered);
      } catch (err) {
        console.error(err);
      }
    }, 150);
    return () => clearTimeout(t);
  }, [customerSearchQuery]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest(".patient-input-container")) {
        setShowPatientResults(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const addToCart = (med) => {
    if (med.stock <= 0) {
      toast.error(`${med.name} is out of stock`);
      return;
    }

    // Resolve allowed selling units from Medicine configuration
    const allowed = med.allowed_selling_units || [med.packing_unit || med.unit || "Strip"];
    const isStripOnly = allowed.length === 1 && allowed[0] === (med.packing_unit || "Strip");
    const unitsPerPack = med.units_per_pack || 10;

    // Prevent purchase if medicine is strip only and stock is less than 1 pack
    if (isStripOnly && med.stock < unitsPerPack) {
      toast.error(`Cannot sell ${med.name}: Stock is less than 1 full ${allowed[0]}.`);
      return;
    }

    if (isExpired(med.expiry_date)) {
      toast.error(`Warning: ${med.name} is EXPIRED (Expiry: ${med.expiry_date})`, { duration: 5000 });
    } else if (isExpiringSoon(med.expiry_date)) {
      toast.warning(`Warning: ${med.name} is expiring soon (Expiry: ${med.expiry_date})`, { duration: 5000 });
    }

    if (med.stock <= med.low_stock_threshold) {
      toast.warning(`Warning: ${med.name} has low stock (Only ${med.stock} remaining)`, { duration: 4000 });
    }

    setCart((c) => {
      const exists = c.find((i) => i.batch_id === med.id);
      if (exists) {
        const currentQty = exists.quantity;
        const currentUnit = exists.selected_selling_unit || exists.packing_unit || "Strip";
        const maxQty = getMaxQuantityForSellingUnit(exists, currentUnit);

        if (currentQty + 1 > maxQty) {
          toast.error("Cannot add more. Stock is insufficient.");
          return c;
        }

        return c.map((i) => (i.batch_id === med.id ? { ...i, quantity: i.quantity + 1 } : i));
      }

      let defaultUnit = allowed[0] || med.packing_unit || "Strip";
      if (defaultUnit === (med.packing_unit || "Strip") && med.stock < unitsPerPack && allowed.includes(med.base_unit || "Tablet")) {
        defaultUnit = med.base_unit || "Tablet";
      }

      return [
        ...c,
        {
          medicine_id: med.medicine_id,
          medicine_name: med.name,
          batch_no: med.batch_no || "",
          batch_id: med.id,
          hsn_code: med.hsn_code || "",
          quantity: 1,
          mrp: med.mrp,
          gst_percent: med.gst_percent,
          discount_percent: 0,
          _stock: med.stock,
          low_stock_threshold: med.low_stock_threshold,
          expiry_date: med.expiry_date,
          // Flexible units fields:
          packing_unit: med.packing_unit || med.unit || "Strip",
          base_unit: med.base_unit || "Tablet",
          units_per_pack: unitsPerPack,
          allowed_selling_units: allowed,
          selected_selling_unit: defaultUnit,
          // Pricing fields:
          purchase_price: med.purchase_price || 0,
          base_unit_selling_price: med.base_unit_selling_price || (med.mrp / unitsPerPack),
          base_unit_purchase_price: med.base_unit_purchase_price || (med.purchase_price / unitsPerPack),
          price_calc_method: med.price_calc_method || "automatic",
        },
      ];
    });
    setQ("");
    setResults([]);
    setShowResults(false);
    setTimeout(() => searchRef.current?.focus(), 0);
  };

  const updateCart = (idx, key, val) => {
    const c = [...cart];
    c[idx] = { ...c[idx], [key]: val };
    setCart(c);
  };

  const updateCartQuantity = (idx, requestedQty) => {
    const item = cart[idx];
    if (!item) return;
    const sellingUnit = item.selected_selling_unit || item.packing_unit || "Strip";
    const maxQty = getMaxQuantityForSellingUnit(item, sellingUnit);
    const numericQty = Number(requestedQty);

    if (!Number.isFinite(numericQty) || numericQty < 1) {
      updateCart(idx, "quantity", 1);
      return;
    }

    const nextQty = Math.floor(numericQty);
    if (maxQty < 1 || nextQty > maxQty) {
      toast.error("Cannot add more. Stock is insufficient.");
      updateCart(idx, "quantity", Math.max(1, maxQty));
      return;
    }

    updateCart(idx, "quantity", nextQty);
  };

  const removeCart = (idx) => setCart(cart.filter((_, i) => i !== idx));

  const totals = useMemo(() => {
    let subtotal = 0;
    let lineDisc = 0;
    for (const it of cart) {
      const isBaseUnit = it.selected_selling_unit === it.base_unit && it.packing_unit !== it.base_unit;
      const unitPrice = isBaseUnit
        ? Number(it.base_unit_selling_price || 0)
        : Number(it.mrp);
      const g = unitPrice * Number(it.quantity);
      subtotal += g;
      lineDisc += g * (Number(it.discount_percent) / 100);
    }
    const afterLine = subtotal - lineDisc;
    const billDiscAmt = afterLine * (Number(billDisc) / 100);
    const netInclTax = afterLine - billDiscAmt;

    // extract GST (MRP is inclusive) — per item
    let taxable = 0;
    let tax = 0;
    for (const it of cart) {
      const isBaseUnit = it.selected_selling_unit === it.base_unit && it.packing_unit !== it.base_unit;
      const unitPrice = isBaseUnit
        ? Number(it.base_unit_selling_price || 0)
        : Number(it.mrp);
      const g = unitPrice * Number(it.quantity);
      const ld = g * (Number(it.discount_percent) / 100);
      const shareAfter = g - ld;
      const proRata = shareAfter * (Number(billDisc) / 100);
      const net = shareAfter - proRata;
      const r = Number(it.gst_percent) / 100;
      const tx = net / (1 + r);
      taxable += tx;
      tax += net - tx;
    }
    return {
      subtotal,
      totalDiscount: lineDisc + billDiscAmt,
      taxable,
      cgst: tax / 2,
      sgst: tax / 2,
      totalTax: tax,
      grandTotal: netInclTax,
    };
  }, [cart, billDisc]);

  const billGrandTotalPaise = useMemo(
    () => Math.round((Number(totals.grandTotal) || 0) * 100),
    [totals.grandTotal]
  );

  const appliedSplitPaise = useMemo(() => {
    if (!appliedSplit || !Array.isArray(appliedSplit)) return 0;
    return appliedSplit.reduce((acc, p) => acc + Math.round((Number(p.amount) || 0) * 100), 0);
  }, [appliedSplit]);

  const isAppliedSplitValid = useMemo(() => {
    if (paymentMode !== "Split") return true;
    if (!appliedSplit || appliedSplit.length < 2 || appliedSplit.length > 3) return false;
    return appliedSplitPaise === billGrandTotalPaise && billGrandTotalPaise > 0;
  }, [paymentMode, appliedSplit, appliedSplitPaise, billGrandTotalPaise]);

  const {
    splitTotalPaidPaise,
    splitRemainingPaise,
    isSplitModalValid,
    splitValidationMessage,
  } = useMemo(() => {
    if (!splitModalOpen) {
      return {
        splitTotalPaidPaise: 0,
        splitRemainingPaise: billGrandTotalPaise,
        isSplitModalValid: false,
        splitValidationMessage: "",
      };
    }

    if (splitRows.length < 2) {
      return {
        splitTotalPaidPaise: 0,
        splitRemainingPaise: billGrandTotalPaise,
        isSplitModalValid: false,
        splitValidationMessage: "Minimum 2 payment methods required.",
      };
    }
    if (splitRows.length > 3) {
      return {
        splitTotalPaidPaise: 0,
        splitRemainingPaise: billGrandTotalPaise,
        isSplitModalValid: false,
        splitValidationMessage: "Maximum 3 payment methods supported.",
      };
    }

    const modes = splitRows.map((r) => r.mode);
    if (new Set(modes).size !== modes.length) {
      return {
        splitTotalPaidPaise: 0,
        splitRemainingPaise: billGrandTotalPaise,
        isSplitModalValid: false,
        splitValidationMessage: "Duplicate payment methods are not allowed.",
      };
    }

    const hasInvalidMode = modes.some((m) => !["Cash", "UPI", "Card"].includes(m));
    if (hasInvalidMode) {
      return {
        splitTotalPaidPaise: 0,
        splitRemainingPaise: billGrandTotalPaise,
        isSplitModalValid: false,
        splitValidationMessage: "Only Cash, UPI, and Card are allowed.",
      };
    }

    let totalPaise = 0;
    for (const r of splitRows) {
      const trimmed = String(r.amount ?? "").trim();
      if (!trimmed) {
        return {
          splitTotalPaidPaise: 0,
          splitRemainingPaise: billGrandTotalPaise,
          isSplitModalValid: false,
          splitValidationMessage: `Please enter an amount for ${r.mode}.`,
        };
      }
      const val = parseFloat(trimmed);
      if (isNaN(val) || val <= 0) {
        return {
          splitTotalPaidPaise: 0,
          splitRemainingPaise: billGrandTotalPaise,
          isSplitModalValid: false,
          splitValidationMessage: `Amount for ${r.mode} must be greater than ₹0.00.`,
        };
      }
      const pPaise = Math.round(val * 100);
      if (pPaise <= 0) {
        return {
          splitTotalPaidPaise: 0,
          splitRemainingPaise: billGrandTotalPaise,
          isSplitModalValid: false,
          splitValidationMessage: `Amount for ${r.mode} must be greater than ₹0.00.`,
        };
      }
      totalPaise += pPaise;
    }

    const diffPaise = billGrandTotalPaise - totalPaise;

    if (diffPaise > 0) {
      return {
        splitTotalPaidPaise: totalPaise,
        splitRemainingPaise: diffPaise,
        isSplitModalValid: false,
        splitValidationMessage: `Remaining amount to allocate: ${inr(diffPaise / 100)}.`,
      };
    }
    if (diffPaise < 0) {
      return {
        splitTotalPaidPaise: totalPaise,
        splitRemainingPaise: diffPaise,
        isSplitModalValid: false,
        splitValidationMessage: `Total exceeds Grand Total by ${inr(Math.abs(diffPaise) / 100)}.`,
      };
    }

    return {
      splitTotalPaidPaise: totalPaise,
      splitRemainingPaise: 0,
      isSplitModalValid: true,
      splitValidationMessage: "Split amounts match Grand Total exactly ✓",
    };
  }, [splitModalOpen, splitRows, billGrandTotalPaise]);

  const onPaymentSelectChange = (newVal) => {
    if (newVal === "Split") {
      if (cart.length === 0) {
        toast.error("Please add medicines to cart before selecting Split Payment");
        return;
      }
      if (totals.grandTotal <= 0) {
        toast.error("Grand Total must be greater than zero for Split Payment");
        return;
      }
      if (appliedSplit && appliedSplit.length >= 2) {
        setSplitRows(appliedSplit.map((s) => ({ mode: s.mode, amount: s.amount.toString() })));
      } else {
        setSplitRows([
          { mode: "Cash", amount: "" },
          { mode: "UPI", amount: "" },
        ]);
      }
      setSplitModalOpen(true);
    } else {
      prevPaymentModeRef.current = newVal;
      setPaymentMode(newVal);
    }
  };

  const handleCancelSplit = () => {
    setSplitModalOpen(false);
    const isCurrentAppliedValid =
      appliedSplit &&
      appliedSplit.length >= 2 &&
      appliedSplit.reduce((acc, p) => acc + Math.round(p.amount * 100), 0) === billGrandTotalPaise;

    if (!isCurrentAppliedValid) {
      if (paymentMode === "Split") {
        setPaymentMode(prevPaymentModeRef.current !== "Split" ? prevPaymentModeRef.current : "Cash");
      }
    }
  };

  const handleApplySplit = () => {
    if (!isSplitModalValid) return;
    const finalPayments = splitRows.map((r) => ({
      mode: r.mode,
      amount: Math.round(parseFloat(r.amount) * 100) / 100,
    }));
    setAppliedSplit(finalPayments);
    setPaymentMode("Split");
    prevPaymentModeRef.current = "Split";
    setSplitModalOpen(false);
  };

  const updateSplitRowMode = (idx, newMode) => {
    setSplitRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, mode: newMode } : r))
    );
  };

  const updateSplitRowAmount = (idx, newAmount) => {
    setSplitRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, amount: newAmount } : r))
    );
  };

  const addSplitRow = () => {
    if (splitRows.length >= 3) return;
    const existingModes = splitRows.map((r) => r.mode);
    const availableMode = ["Cash", "UPI", "Card"].find((m) => !existingModes.includes(m)) || "Card";
    const remaining = splitRemainingPaise > 0 ? (splitRemainingPaise / 100).toFixed(2) : "";
    setSplitRows((prev) => [...prev, { mode: availableMode, amount: remaining }]);
  };

  const removeSplitRow = (idx) => {
    if (splitRows.length <= 2) return;
    setSplitRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (cart.length === 0) return toast.error("Cart is empty");
    if (paymentMode === "Credit" && !selectedPatientId) {
      return toast.error("Please select or quick-add a patient account for Credit Sales");
    }
    if (paymentMode === "Split") {
      if (!appliedSplit || appliedSplit.length < 2) {
        toast.error("Please configure split payment");
        setSplitModalOpen(true);
        return;
      }
      if (!isAppliedSplitValid) {
        toast.error(
          `Split payment total (${inr(appliedSplitPaise / 100)}) does not match Grand Total (${inr(totals.grandTotal)}). Please reconfigure split.`
        );
        setSplitModalOpen(true);
        return;
      }
    }
    const typedQuery = (customerSearchQuery || "").trim();
    const isDigitsOnly = /^\d+$/.test(typedQuery);
    const resolvedName = customer.name || (!isDigitsOnly && typedQuery ? typedQuery : "Walk-in Customer");
    const resolvedPhone = customer.phone || (isDigitsOnly && typedQuery ? typedQuery : "");

    const payload = {
      customer_name: resolvedName,
      customer_phone: resolvedPhone,
      bill_discount_percent: Number(billDisc),
      payment_mode: paymentMode,
      payments: paymentMode === "Split" ? appliedSplit : null,
      patient_id: (resolvedName !== "Walk-in Customer" && customer.name && selectedPatientId) ? selectedPatientId : null,
      due_date: paymentMode === "Credit" ? dueDate : null,
      paid_amount: paymentMode === "Credit" ? Number(paidAmount || 0) : 0,
      payment_notes: paymentMode === "Credit" ? paymentNotes : "",
      items: cart.map((c) => {
        const isBaseUnit = c.selected_selling_unit === c.base_unit && c.packing_unit !== c.base_unit;
        const unitPrice = isBaseUnit
          ? Number(c.base_unit_selling_price || 0)
          : Number(c.mrp);
        const convertedBase = isBaseUnit
          ? Number(c.quantity)
          : Number(c.quantity) * Number(c.units_per_pack || 10);
        return {
          medicine_id: c.medicine_id,
          medicine_name: c.medicine_name,
          batch_no: c.batch_no,
          batch_id: c.batch_id,
          hsn_code: c.hsn_code,
          quantity: Number(c.quantity),
          mrp: Number(unitPrice),
          gst_percent: Number(c.gst_percent),
          discount_percent: Number(c.discount_percent),
          entered_quantity: Number(c.quantity),
          selling_unit: c.selected_selling_unit || c.packing_unit || "Strip",
          converted_base_quantity: Number(convertedBase),
          pack_selling_price: Number(c.mrp),
          base_unit_selling_price: Number(c.base_unit_selling_price || 0),
          pack_purchase_price: Number(c.purchase_price || 0),
          base_unit_purchase_price: Number(c.base_unit_purchase_price || 0),
          price_calc_method: c.price_calc_method || "automatic",
        };
      }),
    };
    try {
      const { data } = await api.post("/bills", payload);
      setLastBill(data);
      toast.success(`Bill ${data.bill_no} created`);
      setPreviewOpen(true);
      // reset
      setCart([]);
      setCustomer({ name: "", phone: "" });
      setSelectedPatientId("");
      setCustomerSearchQuery("");
      setPaidAmount(0);
      setPaymentNotes("");
      setBillDisc(0);
      setPaymentMode("Cash");
      setAppliedSplit(null);
      prevPaymentModeRef.current = "Cash";
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    }
  };

  const printLast = () => {
    setPreviewOpen(true);
  };

  const openQuickAdd = () => {
    setQuickForm({
      name: q.trim(),
      manufacturer: "", batch_no: "", hsn_code: "",
      mrp: 0, purchase_price: 0, gst_percent: 12, stock: 1, low_stock_threshold: 10, expiry_date: "",
    });
    setShowResults(false);
    setQuickOpen(true);
  };

  const saveQuickAdd = async () => {
    if (!quickForm.name.trim()) return toast.error("Name required");
    if (Number(quickForm.mrp) <= 0) return toast.error("MRP must be > 0");
    if (Number(quickForm.stock) <= 0) return toast.error("Stock must be at least 1");
    setQuickSaving(true);
    try {
      const payload = {
        ...quickForm,
        mrp: Number(quickForm.mrp),
        purchase_price: Number(quickForm.purchase_price),
        gst_percent: Number(quickForm.gst_percent),
        stock: Number(quickForm.stock),
        low_stock_threshold: Number(quickForm.low_stock_threshold),
        expiry_date: quickForm.expiry_date || null,
      };
      const { data: created } = await api.post("/medicines", payload);
      toast.success(`Added "${created.name}" to catalogue`);
      setQuickOpen(false);
      const batchItem = {
        id: created.batch_id || created.id,
        medicine_id: created.id,
        name: created.name,
        manufacturer: created.manufacturer,
        hsn_code: created.hsn_code,
        batch_no: created.batch_no,
        expiry_date: created.expiry_date,
        stock: created.stock,
        mrp: created.mrp,
        gst_percent: created.gst_percent,
        low_stock_threshold: created.low_stock_threshold,
        packing_unit: created.packing_unit,
        base_unit: created.base_unit,
        units_per_pack: created.units_per_pack,
        allowed_selling_units: created.allowed_selling_units,
        purchase_price: created.purchase_price,
        base_unit_selling_price: created.base_unit_selling_price,
        base_unit_purchase_price: created.base_unit_purchase_price,
        price_calc_method: created.price_calc_method,
      };
      addToCart(batchItem);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setQuickSaving(false);
    }
  };

  const selectPatient = (p) => {
    setCustomer({ name: p.name || "", phone: p.phone || "" });
    setSelectedPatientId(p.id);
    setCustomerSearchQuery("");
    setShowPatientResults(false);
  };

  const clearCustomer = () => {
    setCustomer({ name: "", phone: "" });
    setSelectedPatientId(null);
    setCustomerSearchQuery("");
    setShowPatientResults(false);
  };

  const openPatientQuickAdd = () => {
    const qStr = customerSearchQuery.trim();
    const isDigitsOnly = /^\d+$/.test(qStr);
    setPatientQuickForm({
      name: isDigitsOnly ? "" : qStr,
      phone: isDigitsOnly ? qStr : "",
      email: "",
      age: "",
      gender: "",
      address: "",
      medical_history: "",
    });
    setShowPatientResults(false);
    setPatientQuickOpen(true);
  };

  const savePatientQuickAdd = async () => {
    if (!patientQuickForm.name.trim()) return toast.error("Name required");
    setPatientQuickSaving(true);
    try {
      const payload = {
        ...patientQuickForm,
        age: patientQuickForm.age ? Number(patientQuickForm.age) : null,
      };
      const { data: created } = await api.post("/patients", payload);
      toast.success(`Added "${created.name}" to patients`);
      setCustomer({ name: created.name, phone: created.phone || "" });
      setSelectedPatientId(created.id);
      setCustomerSearchQuery("");
      setPatientQuickOpen(false);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setPatientQuickSaving(false);
    }
  };

  const renderPatientDropdown = (query) => (
    <div className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border border-border rounded-md max-h-60 overflow-auto shadow-lg">
      {patientResults.length > 0 && (
        <div className="divide-y divide-border">
          {patientResults.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => selectPatient(p)}
              data-testid={`patient-result-${p.id}`}
              className="w-full text-left px-4 py-2.5 hover:bg-muted transition-colors border-b border-border last:border-0"
            >
              <div className="font-medium text-sm text-foreground">{p.name}</div>
              <div className="text-xs text-muted-foreground">
                {p.phone ? `Ph: ${p.phone}` : "No phone"} {p.age ? `· ${p.age} yrs` : ""} {p.gender ? `· ${p.gender}` : ""}
              </div>
            </button>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={openPatientQuickAdd}
        className="w-full text-left flex items-center gap-2 px-4 py-3 bg-primary/5 hover:bg-primary/10 text-primary transition-colors border-t border-border"
      >
        <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground grid place-items-center">
          <Plus size={12} />
        </span>
        <span className="text-xs">
          Not what you need? <span className="font-semibold">Add "{query.trim()}" as a new patient</span>
        </span>
      </button>
    </div>
  );

  return (
    <div className="space-y-4 no-print" data-testid="billing-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Point of Sale</div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: "Chivo" }}>Billing</h1>
        </div>
        {lastBill && (
          <div className="flex items-center gap-2 text-sm">
            <Badge variant="secondary" data-testid="last-bill-no">Last: {lastBill.bill_no}</Badge>
            <Button variant="outline" onClick={printLast} data-testid="reprint-btn">
              <Printer size={14} className="mr-1" /> Preview & Print
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-10 gap-4">
        {/* Left: search + cart */}
        <div className="lg:col-span-7 space-y-4">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={searchRef}
              data-testid="billing-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={() => setShowResults(true)}
              placeholder="Search medicine to add… (name)"
              className="pl-10 h-12 text-base bg-white"
              autoFocus
            />
            {showResults && (q.trim().length > 0) && (
              <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-border rounded-md max-h-80 overflow-auto shadow-sm">
                {results.map((m) => (
                  <button
                    key={m.id}
                    data-testid={`search-result-${m.id}`}
                    onClick={() => addToCart(m)}
                    className="w-full text-left flex items-center px-4 py-2 hover:bg-muted transition-colors border-b border-border last:border-0"
                    disabled={m.stock <= 0}
                  >
                    <div className="flex-1">
                      <div className="font-medium flex items-center flex-wrap gap-1">
                        {m.name}
                        {m.stock <= m.low_stock_threshold && (
                          <Badge variant="destructive" className="bg-amber-500 hover:bg-amber-600 border-none text-[10px] py-0 px-1.5 h-4">
                            Low Stock
                          </Badge>
                        )}
                        {isExpired(m.expiry_date) && (
                          <Badge variant="destructive" className="text-[10px] py-0 px-1.5 h-4">
                            Expired
                          </Badge>
                        )}
                        {!isExpired(m.expiry_date) && isExpiringSoon(m.expiry_date) && (
                          <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 hover:bg-yellow-150 border-none text-[10px] py-0 px-1.5 h-4">
                            Expiring Soon
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {m.manufacturer} · HSN {m.hsn_code || "—"} · Batch {m.batch_no || "—"} · Exp: {formatDate(m.expiry_date)}
                      </div>
                    </div>
                    <div className="text-right tabular-nums">
                      <div className="font-medium">{inr(m.mrp)}</div>
                      <div className={`text-xs ${m.stock <= m.low_stock_threshold ? "text-destructive" : "text-muted-foreground"}`}>
                        Stock: {formatStockByUnits(m.stock, m)}
                        {m.total_stock !== undefined && m.total_stock !== m.stock && (
                          <div>Total: {formatStockByUnits(m.total_stock, m)}</div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
                <button
                  onClick={openQuickAdd}
                  data-testid="quick-add-new-medicine"
                  className="w-full text-left flex items-center gap-2 px-4 py-3 bg-primary/5 hover:bg-primary/10 text-primary transition-colors"
                >
                  <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground grid place-items-center">
                    <Plus size={14} />
                  </span>
                  <span className="text-sm">
                    {results.length === 0
                      ? <>No match. <span className="font-semibold">Add "{q}" as a new medicine</span></>
                      : <>Not what you need? <span className="font-semibold">Add "{q}" as a new medicine</span></>}
                  </span>
                </button>
              </div>
            )}
          </div>

          <div className="rounded-md border border-border bg-white overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Medicine</TableHead>
                  <TableHead>Batch</TableHead>
                  <TableHead>HSN</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">MRP</TableHead>
                  <TableHead className="text-right">Disc%</TableHead>
                  <TableHead className="text-right">GST%</TableHead>
                  <TableHead className="text-right">Line Total</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cart.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                      <Receipt className="mx-auto mb-2 opacity-40" size={24} />
                      Search and add medicines to start a new bill
                    </TableCell>
                  </TableRow>
                )}
                {cart.map((it, idx) => {
                  const isBaseUnit = it.selected_selling_unit === it.base_unit && it.packing_unit !== it.base_unit;
                  const unitPrice = isBaseUnit
                    ? Number(it.base_unit_selling_price || 0)
                    : Number(it.mrp);
                  const line = unitPrice * it.quantity * (1 - it.discount_percent / 100);
                  const maxVal = getMaxQuantityForSellingUnit(it);
                  return (
                    <TableRow key={idx} className="data-row" data-testid={`cart-row-${idx}`}>
                      <TableCell>
                        <div className="font-medium">{it.medicine_name}</div>
                        <div className="text-[10px] text-muted-foreground mt-0.5 leading-tight">
                          Remaining: {formatRemainingStock(it)}
                        </div>
                        {isExpired(it.expiry_date) && (
                          <div className="text-[9px] leading-3 text-destructive font-medium flex items-center gap-2 mt-1" data-testid={`expired-alert-${idx}`}>
                            <AlertTriangle size={12} /> Expired ({it.expiry_date})
                          </div>
                        )}
                        {!isExpired(it.expiry_date) && isExpiringSoon(it.expiry_date) && (
                          <div className="text-[9px] leading-3 text-amber-600 font-medium flex items-center gap-2 mt-1" data-testid={`expiring-alert-${idx}`}>
                            <Clock size={12} /> Expiring soon ({it.expiry_date})
                          </div>
                        )}
                        {it._stock <= it.low_stock_threshold && (
                          <div className="text-[9px] leading-3 text-amber-600 font-medium flex items-center gap-1 mt-0.5" data-testid={`low-stock-alert-${idx}`}>
                            <AlertTriangle size={12} /> Low stock ({it._stock} left)
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{it.batch_no || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{it.hsn_code || "—"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2 select-none">
                          <button
                            type="button"
                            onClick={() => updateCartQuantity(idx, Math.max(1, it.quantity - 1))}
                            className="h-4 w-4 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded flex items-center justify-center"
                            disabled={it.quantity <= 1}
                          >
                            <Minus size={12} />
                          </button>
                          <Input
                            type="number"
                            min="1"
                            max={maxVal}
                            step="1"
                            className="h-7 w-12 text-center p-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            value={it.quantity}
                            onChange={(e) => updateCartQuantity(idx, e.target.value)}
                            onWheel={(e) => e.currentTarget.blur()}
                            data-testid={`cart-qty-${idx}`}
                          />
                          <button
                            type="button"
                            onClick={() => updateCartQuantity(idx, it.quantity + 1)}
                            className="h-4 w-4 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded flex items-center justify-center"
                            disabled={it.quantity >= maxVal}
                          >
                            <Plus size={12} />
                          </button>

                          {/* Dynamic selling unit selector */}
                          {it.allowed_selling_units && it.allowed_selling_units.length > 1 ? (
                            <select
                              value={it.selected_selling_unit || it.packing_unit || "Strip"}
                              onChange={(e) => {
                                const newUnit = e.target.value;
                                const newMax = getMaxQuantityForSellingUnit(it, newUnit);
                                const clampedQty = Math.max(1, Math.min(newMax, it.quantity));
                                const c = [...cart];
                                c[idx] = { ...c[idx], selected_selling_unit: newUnit, quantity: clampedQty };
                                setCart(c);
                                if (it.quantity > newMax) toast.error("Cannot add more. Stock is insufficient.");
                              }}
                              className="h-7 rounded border border-input bg-background px-1 text-xs focus-visible:outline-none"
                            >
                              {it.allowed_selling_units.map((u) => (
                                <option key={u} value={u}>{u}</option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-xs text-muted-foreground min-w-[40px] text-left">
                              {it.selected_selling_unit || it.packing_unit || "Strip"}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{inr(unitPrice)}</TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.5"
                          className="h-8 w-16 text-right ml-auto"
                          value={it.discount_percent}
                          onChange={(e) => updateCart(idx, "discount_percent", Number(e.target.value))}
                        />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {it.gst_percent}%
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">{inr(line)}</TableCell>
                      <TableCell>
                        <button onClick={() => removeCart(idx)} className="text-destructive p-1 hover:bg-muted rounded" data-testid={`remove-${idx}`}>
                          <Trash2 size={14} />
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Right: checkout */}
        <div className="lg:col-span-3">
          <div className="rounded-md border border-border bg-white p-5 space-y-4 sticky top-6">
            <div className="patient-input-container relative">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center justify-between">
                <span>Patient / Customer</span>
                {customer.name && (
                  <span className="text-[11px] text-primary font-medium">Selected</span>
                )}
              </div>

              {customer.name || selectedPatientId ? (
                <div className="flex items-center justify-between p-2.5 rounded-md border border-primary/30 bg-primary/5 text-xs">
                  <div className="min-w-0 flex-1 pr-2">
                    <div className="font-semibold text-foreground truncate">{customer.name}</div>
                    <div className="text-muted-foreground text-[11px] truncate">
                      {customer.phone ? `Ph: ${customer.phone}` : "No phone number"}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive shrink-0"
                    onClick={clearCustomer}
                    title="Clear customer (revert to Walk-in)"
                    data-testid="clear-customer-button"
                  >
                    <X size={14} />
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <Input
                    placeholder="Search customer by name or phone..."
                    value={customerSearchQuery}
                    onChange={(e) => {
                      setCustomerSearchQuery(e.target.value);
                      setShowPatientResults(true);
                    }}
                    onFocus={() => {
                      if (customerSearchQuery.trim()) setShowPatientResults(true);
                    }}
                    data-testid="customer-search-input"
                  />
                  {showPatientResults && customerSearchQuery.trim().length > 0 && renderPatientDropdown(customerSearchQuery)}
                </div>
              )}
            </div>

            <div className="border-t border-border pt-4 space-y-2 text-sm tabular-nums">
              <Row label="Subtotal (MRP)" value={inr(totals.subtotal)} />
              <Row label="Discount" value={`− ${inr(totals.totalDiscount)}`} />
              {shopInfo?.tax_applicable !== false && (
                <div className="border-t border-border pt-2 space-y-1">
                  <Row label="Taxable" value={inr(totals.taxable)} muted />
                  <Row label="CGST" value={inr(totals.cgst)} muted />
                  <Row label="SGST" value={inr(totals.sgst)} muted />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-muted-foreground">Bill Disc %</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={billDisc}
                  onChange={(e) => setBillDisc(e.target.value)}
                  data-testid="bill-discount"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Payment</Label>
                <Select value={paymentMode} onValueChange={onPaymentSelectChange}>
                  <SelectTrigger data-testid="payment-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="UPI">UPI</SelectItem>
                    <SelectItem value="Card">Card</SelectItem>
                    <SelectItem value="Credit">Credit</SelectItem>
                    <SelectItem value="Split">Split</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {paymentMode === "Split" && (
              <div
                className={`p-2.5 rounded border text-xs ${
                  isAppliedSplitValid
                    ? "bg-muted/30 border-border"
                    : "bg-destructive/10 border-destructive/30"
                }`}
                data-testid="split-summary-box"
              >
                {isAppliedSplitValid ? (
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-foreground flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block shrink-0"></span>
                        <span>Split Payment</span>
                      </div>
                      <div className="text-muted-foreground font-mono text-[11px] truncate mt-0.5">
                        {appliedSplit?.map((p) => `${p.mode} ${inr(p.amount)}`).join(" + ")}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 px-2.5 text-xs shrink-0"
                      onClick={() => {
                        setSplitRows(appliedSplit.map((s) => ({ mode: s.mode, amount: s.amount.toString() })));
                        setSplitModalOpen(true);
                      }}
                      data-testid="edit-split-btn"
                    >
                      Edit
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between gap-2 text-destructive font-medium">
                      <span className="flex items-center gap-1">
                        <AlertTriangle size={13} className="shrink-0" />
                        Split Mismatch ({inr(appliedSplitPaise / 100)} vs {inr(totals.grandTotal)})
                      </span>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="h-7 px-2.5 text-xs shrink-0"
                        onClick={() => {
                          setSplitRows(
                            appliedSplit
                              ? appliedSplit.map((s) => ({ mode: s.mode, amount: s.amount.toString() }))
                              : [
                                  { mode: "Cash", amount: "" },
                                  { mode: "UPI", amount: "" },
                                ]
                          );
                          setSplitModalOpen(true);
                        }}
                        data-testid="reconfigure-split-btn"
                      >
                        Reconfigure
                      </Button>
                    </div>
                    <p className="text-[10px] text-muted-foreground leading-tight">
                      Bill total changed. Please adjust split amounts to match the new total.
                    </p>
                  </div>
                )}
              </div>
            )}

            {paymentMode === "Credit" && (
              <div className="border border-border p-3 rounded bg-muted/20 space-y-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Credit Details</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Due Date</Label>
                    <Input
                      type="date"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                      data-testid="credit-due-date"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Paid Amount (₹)</Label>
                    <Input
                      type="number"
                      min="0"
                      max={totals.grandTotal}
                      value={paidAmount}
                      onChange={(e) => setPaidAmount(Math.min(totals.grandTotal, Math.max(0, parseFloat(e.target.value) || 0)))}
                      data-testid="credit-paid-amount"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Payment Notes</Label>
                  <Input
                    placeholder="e.g. Partial cash payment details"
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                    data-testid="credit-payment-notes"
                  />
                </div>
              </div>
            )}

            <div className="rounded-md bg-primary text-primary-foreground p-4 flex items-baseline justify-between">
              <span className="text-xs uppercase tracking-widest opacity-80">Grand Total</span>
              <span className="text-3xl font-semibold tabular-nums" data-testid="grand-total" style={{ fontFamily: "Chivo" }}>
                {inr(totals.grandTotal)}
              </span>
            </div>

            <Button
              className="w-full h-11"
              onClick={submit}
              data-testid="checkout-btn"
              disabled={cart.length === 0 || (paymentMode === "Split" && !isAppliedSplitValid)}
            >
              {paymentMode === "Split" && !isAppliedSplitValid && appliedSplit
                ? "Reconfigure Split to Save"
                : "Save & Preview Bill"}
            </Button>
          </div>
        </div>
      </div>

      {/* Split Payment Modal */}
      <Dialog
        open={splitModalOpen}
        onOpenChange={(open) => {
          if (!open) handleCancelSplit();
          else setSplitModalOpen(true);
        }}
      >
        <DialogContent className="max-w-md" data-testid="split-payment-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt size={18} className="text-primary" />
              <span>Split Payment</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-1">
            {/* Grand Total Banner */}
            <div className="rounded-lg bg-primary/10 border border-primary/20 p-3.5 flex items-center justify-between">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block">
                  Bill Grand Total
                </span>
                <span className="text-2xl font-bold tabular-nums text-foreground" style={{ fontFamily: "Chivo" }}>
                  {inr(totals.grandTotal)}
                </span>
              </div>
              <div className="text-right">
                <span className="text-xs font-medium text-muted-foreground block">Allowed Methods</span>
                <span className="text-xs font-semibold text-primary">Cash · UPI · Card</span>
              </div>
            </div>

            {/* Payment Rows */}
            <div className="space-y-2.5">
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Payment Breakdown ({splitRows.length} of 3 max)
              </Label>
              {splitRows.map((row, idx) => {
                const isModeUsedElsewhere = (m) => splitRows.some((r, i) => i !== idx && r.mode === m);
                return (
                  <div
                    key={idx}
                    className="flex items-center gap-2 p-2 rounded-md border border-border bg-card shadow-sm"
                    data-testid={`split-row-${idx}`}
                  >
                    <div className="w-[130px] shrink-0">
                      <Select
                        value={row.mode}
                        onValueChange={(val) => updateSplitRowMode(idx, val)}
                      >
                        <SelectTrigger data-testid={`split-mode-select-${idx}`} className="h-9 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {["Cash", "UPI", "Card"].map((opt) => (
                            <SelectItem key={opt} value={opt} disabled={isModeUsedElsewhere(opt)}>
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex-1 relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-mono">
                        ₹
                      </span>
                      <Input
                        type="number"
                        min="0.01"
                        step="0.01"
                        placeholder="0.00"
                        className="h-9 pl-6 text-sm tabular-nums font-mono"
                        value={row.amount}
                        onChange={(e) => updateSplitRowAmount(idx, e.target.value)}
                        data-testid={`split-amount-input-${idx}`}
                      />
                    </div>

                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0"
                      disabled={splitRows.length <= 2}
                      onClick={() => removeSplitRow(idx)}
                      title={splitRows.length <= 2 ? "Minimum 2 payment rows required" : "Remove payment method"}
                      data-testid={`split-remove-row-${idx}`}
                    >
                      <Trash2 size={15} />
                    </Button>
                  </div>
                );
              })}
            </div>

            {/* Add Payment Method Button */}
            {splitRows.length < 3 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full border-dashed h-8 text-xs gap-1 text-muted-foreground hover:text-foreground"
                onClick={addSplitRow}
                data-testid="add-split-method-btn"
              >
                <Plus size={14} /> Add Payment Method
              </Button>
            )}

            {/* Calculations Summary Box */}
            <div className="rounded-md border border-border bg-muted/40 p-3 space-y-1.5 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Total Paid:</span>
                <span className="font-bold font-mono text-sm tabular-nums">
                  {inr(splitTotalPaidPaise / 100)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Remaining Amount:</span>
                <span
                  className={`font-bold font-mono text-sm tabular-nums ${
                    splitRemainingPaise === 0
                      ? "text-emerald-600"
                      : splitRemainingPaise > 0
                      ? "text-amber-600"
                      : "text-destructive"
                  }`}
                >
                  {inr(Math.abs(splitRemainingPaise) / 100)}
                  {splitRemainingPaise < 0 ? " (Excess)" : ""}
                </span>
              </div>
            </div>

            {/* Inline validation status */}
            <div className="min-h-[22px]">
              {splitValidationMessage && (
                <div
                  className={`text-xs font-medium flex items-center gap-1.5 ${
                    isSplitModalValid ? "text-emerald-600" : "text-destructive"
                  }`}
                  data-testid="split-validation-msg"
                >
                  {isSplitModalValid ? (
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 inline-block"></span>
                  ) : (
                    <AlertTriangle size={14} className="shrink-0" />
                  )}
                  <span>{splitValidationMessage}</span>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancelSplit}
              data-testid="split-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!isSplitModalValid}
              onClick={handleApplySplit}
              data-testid="split-apply-btn"
            >
              Apply Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview modal — also contains the hidden print-area for window.print() */}
      <InvoicePreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        bill={lastBill}
        shop={shopInfo}
      />

      {/* Printable invoice (fallback, only visible during print) */}
      {lastBill && !previewOpen && <InvoicePrint bill={lastBill} shop={shopInfo} />}

      {/* Quick-add medicine from billing search */}
      <Dialog open={quickOpen} onOpenChange={setQuickOpen}>
        <DialogContent className="max-w-xl" data-testid="quick-add-dialog">
          <DialogHeader>
            <DialogTitle>Add New Medicine to Catalogue</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <QF label="Name *"><Input data-testid="quick-name" value={quickForm.name} onChange={(e) => setQuickForm({ ...quickForm, name: e.target.value })} autoFocus /></QF>
            <QF label="Manufacturer"><Input value={quickForm.manufacturer} onChange={(e) => setQuickForm({ ...quickForm, manufacturer: e.target.value })} /></QF>
            <QF label="HSN Code"><Input value={quickForm.hsn_code} onChange={(e) => setQuickForm({ ...quickForm, hsn_code: e.target.value })} /></QF>
            <QF label="Batch No."><Input value={quickForm.batch_no} onChange={(e) => setQuickForm({ ...quickForm, batch_no: e.target.value })} /></QF>
            <QF label="MRP (₹) *"><Input data-testid="quick-mrp" type="number" step="0.01" value={quickForm.mrp} onChange={(e) => setQuickForm({ ...quickForm, mrp: e.target.value })} /></QF>
            <QF label="Purchase Price (₹)"><Input type="number" step="0.01" value={quickForm.purchase_price} onChange={(e) => setQuickForm({ ...quickForm, purchase_price: e.target.value })} /></QF>
            <QF label="GST %"><Input type="number" step="0.5" value={quickForm.gst_percent} onChange={(e) => setQuickForm({ ...quickForm, gst_percent: e.target.value })} /></QF>
            <QF label="Opening Stock *"><Input data-testid="quick-stock" type="number" value={quickForm.stock} onChange={(e) => setQuickForm({ ...quickForm, stock: e.target.value })} /></QF>
            <QF label="Low Stock Alert"><Input type="number" value={quickForm.low_stock_threshold} onChange={(e) => setQuickForm({ ...quickForm, low_stock_threshold: e.target.value })} /></QF>
            <QF label="Expiry Date"><Input type="date" value={quickForm.expiry_date || ""} onChange={(e) => setQuickForm({ ...quickForm, expiry_date: e.target.value })} /></QF>
          </div>
          <div className="text-xs text-muted-foreground">The medicine will be saved to your catalogue and instantly added to the current cart.</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickOpen(false)}>Cancel</Button>
            <Button onClick={saveQuickAdd} disabled={quickSaving} data-testid="quick-save">
              {quickSaving ? "Saving…" : "Save & Add to Bill"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick-add patient from billing search */}
      <Dialog open={patientQuickOpen} onOpenChange={setPatientQuickOpen}>
        <DialogContent className="max-w-xl" data-testid="patient-quick-add-dialog">
          <DialogHeader>
            <DialogTitle>Add New Patient</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <QF label="Name *">
                <Input
                  data-testid="patient-quick-name"
                  value={patientQuickForm.name}
                  onChange={(e) => setPatientQuickForm({ ...patientQuickForm, name: e.target.value })}
                  autoFocus
                />
              </QF>
            </div>
            <QF label="Phone">
              <Input
                data-testid="patient-quick-phone"
                value={patientQuickForm.phone}
                onChange={(e) => setPatientQuickForm({ ...patientQuickForm, phone: e.target.value })}
              />
            </QF>
            <QF label="Email">
              <Input
                value={patientQuickForm.email}
                onChange={(e) => setPatientQuickForm({ ...patientQuickForm, email: e.target.value })}
                type="email"
              />
            </QF>
            <QF label="Age (Years)">
              <Input
                value={patientQuickForm.age}
                onChange={(e) => setPatientQuickForm({ ...patientQuickForm, age: e.target.value })}
                type="number"
                min="0"
                max="150"
              />
            </QF>
            <QF label="Gender">
              <Select
                value={patientQuickForm.gender}
                onValueChange={(val) => setPatientQuickForm({ ...patientQuickForm, gender: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </QF>
            <div className="col-span-2">
              <QF label="Address">
                <Input
                  value={patientQuickForm.address}
                  onChange={(e) => setPatientQuickForm({ ...patientQuickForm, address: e.target.value })}
                />
              </QF>
            </div>
            <div className="col-span-2">
              <QF label="Medical History">
                <Input
                  value={patientQuickForm.medical_history}
                  onChange={(e) => setPatientQuickForm({ ...patientQuickForm, medical_history: e.target.value })}
                  placeholder="Allergies, chronic diseases, or history notes"
                />
              </QF>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPatientQuickOpen(false)}>Cancel</Button>
            <Button onClick={savePatientQuickAdd} disabled={patientQuickSaving} data-testid="patient-quick-save">
              {patientQuickSaving ? "Saving…" : "Save & Add to Bill"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QF({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Row({ label, value, muted }) {
  return (
    <div className={`flex justify-between ${muted ? "text-muted-foreground text-xs" : ""}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
